import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LOGO_URL } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Loader2, LogOut, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const MIN_LEN = 8;

/**
 * Forced password rotation screen.
 *
 * Operators are seeded with a placeholder password from env vars and
 * `must_change_password=true`. This screen is the only page they can reach
 * until they rotate to a real password. Once successful the backend clears the
 * flag and reissues the auth cookies; we refresh the auth context and redirect
 * to the dashboard.
 */
export default function ChangePassword() {
    const { user, loading, refresh, logout } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (next.length < MIN_LEN) { setError(`New password must be at least ${MIN_LEN} characters`); return; }
        if (next === current) { setError("New password must be different from current"); return; }
        if (next !== confirm) { setError("New password and confirmation do not match"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
            // Store the reissued bearer so subsequent requests authenticate
            // even when cookies are blocked by cross-site policies.
            if (data?.access_token) {
                const { setToken } = await import("@/lib/api");
                setToken(data.access_token);
            }
            await refresh();   // pulls the cleared `must_change_password` flag
            toast.success("Password updated");
            navigate("/", { replace: true });
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const isForced = !!user.must_change_password;

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="w-full max-w-sm" data-testid="change-password-page">
                <div className="flex flex-col items-center mb-6">
                    <img src={LOGO_URL} alt="A+ Student Loans" className="h-14 w-14 rounded-md object-contain" />
                    <h1 className="mt-4 text-xl font-display font-semibold tracking-tight text-gray-900">
                        {isForced ? "Set a new password" : "Change password"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 text-center">
                        Signed in as <span className="font-medium text-gray-800">{user.email}</span>
                    </p>
                </div>

                {isForced && (
                    <div className="flex gap-2 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="forced-rotation-notice">
                        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>You're signed in with a placeholder password. Set a permanent one before you can use the app.</span>
                    </div>
                )}

                <form
                    onSubmit={submit}
                    className="bg-white border border-border rounded-xl shadow-sm p-6 space-y-4"
                    data-testid="change-password-form"
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="current">Current password</Label>
                        <Input id="current" type="password" autoComplete="current-password" required
                            value={current} onChange={e => setCurrent(e.target.value)}
                            data-testid="current-password-input" />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="next">New password</Label>
                        <Input id="next" type="password" autoComplete="new-password" required minLength={MIN_LEN}
                            value={next} onChange={e => setNext(e.target.value)}
                            data-testid="new-password-input" />
                        <p className="text-[11px] text-muted-foreground">Minimum {MIN_LEN} characters. Avoid the placeholder.</p>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="confirm">Confirm new password</Label>
                        <Input id="confirm" type="password" autoComplete="new-password" required
                            value={confirm} onChange={e => setConfirm(e.target.value)}
                            data-testid="confirm-password-input" />
                    </div>
                    {error && (
                        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="change-password-error">
                            {error}
                        </div>
                    )}
                    <Button type="submit" className="w-full" disabled={busy} data-testid="change-password-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                    </Button>
                </form>

                {isForced && (
                    <div className="text-center mt-4">
                        <button
                            onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
                            className="text-xs text-muted-foreground hover:text-gray-900 inline-flex items-center gap-1"
                            data-testid="change-password-signout"
                        >
                            <LogOut className="h-3 w-3" /> Sign out instead
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
